import { View, Text, Pressable, Modal } from 'react-native';
import { useState } from 'react';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useColors } from '../lib/colors';
import { useUnregisterPushNotifications } from '../hooks/usePushNotifications';
import { performSignOut } from '../lib/sign-out';
import { HelpSupportModal } from './HelpSupportModal';

function getInitials(firstName: string | null | undefined, lastName: string | null | undefined): string {
  const first = (firstName ?? '').trim()[0] ?? '';
  const last = (lastName ?? '').trim()[0] ?? '';
  return (first + last).toUpperCase() || '?';
}

interface AvatarMenuProps {
  firstName?: string | null;
  lastName?: string | null;
  /** Source label included in the auth.signed_out event. */
  source: string;
  /** Current route, included in the auth.signed_out event. */
  route?: string;
  /** Hide profile navigation when the app is in a restricted access state. */
  showProfile?: boolean;
  /** Hide integrations navigation when the app is in a restricted access state. */
  showIntegrations?: boolean;
  /** Disable Help & Support when the app is showing a read-only demo. */
  disableSupport?: boolean;
}

/**
 * Round avatar (initials) that opens a popover with Profile / Settings /
 * Sign out. Used in the mobile top bar so the menu is reachable from any
 * authenticated screen.
 */
export function AvatarMenu({
  firstName,
  lastName,
  source,
  route,
  showProfile = true,
  showIntegrations = true,
  disableSupport = false,
}: AvatarMenuProps) {
  const router = useRouter();
  const c = useColors();
  const insets = useSafeAreaInsets();
  const [menuOpen, setMenuOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const unregisterPushNotifications = useUnregisterPushNotifications();

  // MobileTopBar lays out: safe-area top + pt-2 (8) + 40px avatar.
  // Drop the menu just below the avatar with a small gap so it never
  // covers the trigger on devices with a notch / dynamic island.
  const menuTop = insets.top + 8 + 40 + 8;

  const handleSignOut = async () => {
    setMenuOpen(false);
    await performSignOut({
      source,
      ...(route ? { route } : {}),
      unregisterPushNotifications,
    });
    router.replace('/(auth)/login');
  };

  return (
    <>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Open account menu"
        onPress={() => setMenuOpen(true)}
        className="h-10 w-10 items-center justify-center rounded-full bg-forest-600"
      >
        <Text className="text-sm font-semibold text-white">
          {getInitials(firstName, lastName)}
        </Text>
      </Pressable>

      <Modal
        visible={menuOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setMenuOpen(false)}
      >
        <Pressable
          className="flex-1 bg-black/40"
          onPress={() => setMenuOpen(false)}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="absolute right-4 w-56 rounded-2xl bg-white dark:bg-gray-800 shadow-lg overflow-hidden"
            style={{ top: menuTop }}
          >
            {showProfile ? (
              <MenuRow
                icon="person-outline"
                label="Profile"
                color={c.text}
                onPress={() => {
                  setMenuOpen(false);
                  router.push('/(app)/profile');
                }}
              />
            ) : null}
            {showIntegrations ? (
              <MenuRow
                icon="layers-outline"
                label="Integrations"
                color={c.text}
                onPress={() => {
                  setMenuOpen(false);
                  router.push('/(app)/integrations');
                }}
              />
            ) : null}
            <MenuRow
              icon="settings-outline"
              label="Settings"
              color={c.text}
              onPress={() => {
                setMenuOpen(false);
                router.push('/(app)/settings');
              }}
            />
            <MenuRow
              icon="help-circle-outline"
              label="Help & Support"
              color={c.text}
              disabled={disableSupport}
              onPress={() => {
                setMenuOpen(false);
                setSupportOpen(true);
              }}
            />
            <View className="h-px bg-gray-100 dark:bg-gray-700" />
            <MenuRow
              icon="log-out-outline"
              label="Sign out"
              color="#dc2626"
              onPress={handleSignOut}
            />
          </Pressable>
        </Pressable>
      </Modal>
      <HelpSupportModal visible={supportOpen} onClose={() => setSupportOpen(false)} />
    </>
  );
}

function MenuRow({
  icon,
  label,
  color,
  disabled = false,
  onPress,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  label: string;
  color: string;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={() => {
        if (!disabled) onPress();
      }}
      disabled={disabled}
      className={`flex-row items-center gap-3 px-4 py-3 active:bg-gray-50 dark:active:bg-gray-700 ${
        disabled ? 'opacity-40' : ''
      }`}
    >
      <Ionicons name={icon} size={18} color={color} />
      <Text className="text-sm font-medium" style={{ color }}>
        {label}
      </Text>
    </Pressable>
  );
}
